import React, { useEffect, useMemo, useState } from "react";
import { GoBell } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useNotification } from "../contexts/NotificationContext";

const NotificationBell = ({ userId }) => {
  const navigate = useNavigate();
  const {
    notificationData,
    status,
    handleMarkAllAsRead,
    updateNotification,
  } = useNotification();
  


  const unreadNotifications = useMemo(
    () => notificationData.filter((notification) => !notification.is_read),
    [notificationData]
  );

  const unreadCount = unreadNotifications.length;

  const groupedNotifications = useMemo(() => {
    const groups = {};
    unreadNotifications.forEach((n) => {
      const date = moment(n.createdAt).format("YYYY-MM-DD");
      if (!groups[date]) groups[date] = [];
      groups[date].push(n);
    });

    return Object.entries(groups).map(([date, notifs]) => ({
      dateLabel: moment(date).calendar(null, {
        sameDay: "[Today]",
        lastDay: "[Yesterday]",
        lastWeek: "dddd",
        sameElse: "DD MMM YYYY",
      }),
      notifications: notifs,
    }));
  }, [unreadNotifications]);

  return (
    <div className="relative group">
      <div className="text-xl cursor-pointer hover:text-red-500 transition-all relative">
        <GoBell />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </div>
      
      {/* Dropdown */}
      <div
        className={`
          absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl z-50 border 
          opacity-0 invisible group-hover:opacity-100 group-hover:visible
          transition-all duration-200 ease-out transform group-hover:translate-y-0 translate-y-1
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-4 py-3 border-b border-slate-300 text-sm font-semibold text-primary">
          <span>Notifications</span>
          <div className="space-x-2 text-xs text-blue-400">
            <button
              onClick={handleMarkAllAsRead}
              className="hover:underline cursor-pointer"
              disabled={notificationData.length === 0}
            >
              Mark all as read
            </button>
            <button
              onClick={() => navigate("/store/notifications")}
              className="hover:underline cursor-pointer"
            >
              View all
            </button>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto text-sm text-black">
          {unreadCount > 0 ? (
            groupedNotifications.map((group, i) => (
              <div key={i}>
                <div className="sticky top-0 px-4 py-1 bg-gray-100 text-xs font-medium text-gray-500 z-10">
                  {group.dateLabel}
                </div>
                {group.notifications.map((n) => (
                  <div
                    key={n._id}
                    className="p-3 border-b border-gray-200 bg-blue-50 cursor-pointer"
                    onClick={() => updateNotification(n._id)}
                  >
                    <div className="flex space-x-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100 text-blue-600">
                          <GoBell className="text-lg" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {n.title}
                          </p>
                          <span className="text-xs text-gray-500">
                            {moment(n.createdAt).format("h:mm A")}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {n.message}
                          {n.orderId && (
                            <span className="font-semibold text-blue-600">
                              {" "}
                              Order #{n.orderId}
                            </span>
                          )}
                        </p>
                        {n.reason && (
                          <div className="mt-2 p-2 bg-white/50 rounded border border-gray-200 text-sm">
                            Reason: {n.reason}
                          </div>
                        )}
                        <div className="mt-2 flex justify-between">
                          <span className="text-xs text-gray-500">
                            {moment(n.createdAt).fromNow()}
                          </span>
                          {n.type === "order_cancelled" && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                              Urgent
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 mb-3 text-gray-300">
                <GoBell size={64} />
              </div>
              <h3 className="text-base font-medium text-gray-700">
                No unread notifications
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                You're all caught up! Check back later for updates.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationBell;